import { useState, type ReactNode } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@baluu/ui/components/avatar";
import { Button } from "@baluu/ui/components/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@baluu/ui/components/card";
import { NoteMarkdown } from "@baluu/ui/components/checkout/note-markdown";

// The manage page's small building blocks, ports of v1's
// AvatarSpeechBubbleBanner, PromptCard and GetReadyTab's BulletPointsList.
// v1's Obby background artwork and fixed brand colours are left out: on v2
// the site's own theme (bookingComponentCss) colours them.

/** The host's note to the customer (before or after the class), signed
 * with their name and picture. The note is the school's Markdown. */
export function HostNote({ title, text, name, avatar }: { title: string; text?: string; name?: string; avatar?: string }) {
  return (
    <Card className="border-border/60 shadow-sm">
      <CardContent className="flex flex-col gap-4 p-5 sm:flex-row">
        <Avatar className="h-14 w-14 shrink-0">
          {avatar && <AvatarImage src={avatar} alt={name ?? ""} />}
          <AvatarFallback className="bg-primary text-primary-foreground">{name?.charAt(0)?.toUpperCase() ?? "?"}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 space-y-3">
          <p className="text-base font-semibold">{title}</p>
          {text ? <NoteMarkdown className="space-y-2 text-sm leading-relaxed text-foreground">{text}</NoteMarkdown> : null}
          {name && <p className="text-sm font-medium text-muted-foreground">{name}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

/** A card asking for one thing, with its button (a link). */
export function PromptCard({ title, text, icon, action }: { title: string; text: string; icon?: ReactNode; action: ReactNode }) {
  return (
    <Card className="border-t-4 border-t-primary border-border/60 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          {title}
          {icon}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm leading-relaxed text-muted-foreground">{text}</p>
        {action}
      </CardContent>
    </Card>
  );
}

/** "What you will learn / need": the first three, then "Read more". */
export function BulletList({ title, items, max = 3 }: { title: string; items: string[]; max?: number }) {
  const [open, setOpen] = useState(false);
  const shown = open ? items : items.slice(0, max);
  return (
    <Card className="border-l-4 border-l-primary border-border/60 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {shown.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-2 inline-block h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
              <span className="text-sm leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      {items.length > max && (
        <CardFooter>
          <Button variant="link" className="h-auto p-0" onClick={() => setOpen(!open)} aria-expanded={open}>
            Read {open ? "less" : "more"}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
